/**
 * Authentication Service
 * Wraps Supabase Auth for user authentication
 */

import { SUPABASE_URL, SUPABASE_ANON_KEY, isConfigured } from '../config/config.js';

class AuthService {
  constructor() {
    this.supabase = null;
    this.currentUser = null;
    this.session = null;
    this.listeners = new Set();
    this.initialized = false;
    this.authSubscription = null;
    this.pendingPasswordRecovery = false; // Track if PASSWORD_RECOVERY event fired before listeners registered

    // Check URL hash for recovery token BEFORE Supabase processes it
    // This must happen in constructor since Supabase clears the hash during init
    this.checkUrlForRecoveryToken();
  }

  /**
   * Check URL for recovery token
   * Sets flag but does NOT clean URL yet - Supabase needs to process it first
   */
  checkUrlForRecoveryToken() {
    // Check query parameter (survives PKCE redirect)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('password_recovery') === 'true') {
      console.log('[AuthService] Password recovery mode detected');
      this.pendingPasswordRecovery = true;
      // DON'T clean URL here - Supabase needs to process the code parameter first
      return;
    }

    // Also check hash fragment (for non-PKCE flows or as fallback)
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      console.log('[AuthService] Password recovery mode detected (hash)');
      this.pendingPasswordRecovery = true;
    }
  }

  /**
   * Clean up recovery URL parameters after Supabase has processed them
   */
  cleanupRecoveryUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('password_recovery') === 'true') {
      const cleanUrl = window.location.pathname;
      history.replaceState(null, '', cleanUrl);
      console.log('[AuthService] Cleaned up recovery URL');
    }
  }

  /**
   * Initialize the auth service
   * Must be called after Supabase SDK is loaded
   */
  async init() {
    if (this.initialized) return;

    if (!isConfigured()) {
      console.warn('[AuthService] Supabase not configured. Running in anonymous mode.');
      this.initialized = true;
      return;
    }

    if (typeof supabase === 'undefined' || !supabase.createClient) {
      console.error('[AuthService] Supabase SDK not loaded');
      this.initialized = true; // Prevent repeated attempts
      return;
    }

    // Clean up any existing subscription
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }

    try {
      // If in password recovery mode, clear any existing session from localStorage
      // This ensures the recovery session can be properly established
      if (this.pendingPasswordRecovery) {
        localStorage.removeItem('visualizer-auth');
      }

      this.supabase = supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          // Use localStorage instead of lock-based storage to avoid LockManager errors
          storageKey: 'visualizer-auth',
          storage: window.localStorage,
          flowType: 'pkce',
          detectSessionInUrl: true,
          persistSession: true,
          autoRefreshToken: true
        }
      });

      // Get initial session
      const { data: { session }, error } = await this.supabase.auth.getSession();
      if (error) throw error;

      this.session = session;
      this.currentUser = session?.user || null;

      // Clean up recovery URL after Supabase has processed the code parameter
      if (this.pendingPasswordRecovery) {
        this.cleanupRecoveryUrl();
      }

      // Auto-create anonymous user if no session exists
      // This ensures every visitor has a tracked identity for server-side usage tracking
      // Skip if in password recovery mode - recovery session should be used instead
      if (!this.currentUser && !this.pendingPasswordRecovery) {
        try {
          console.log('[AuthService] No session, creating anonymous user...');
          const { user, session: anonSession } = await this.signInAnonymously();
          this.session = anonSession;
          this.currentUser = user;
          console.log('[AuthService] Anonymous user created:', this.currentUser?.id);
        } catch (anonError) {
          console.warn('[AuthService] Failed to create anonymous user:', anonError);
          // Continue without anonymous auth - will fall back to client-side anonymous mode
        }
      }

      // Listen for auth state changes
      const { data: { subscription } } = this.supabase.auth.onAuthStateChange((event, session) => {
        try {
          this.session = session;
          this.currentUser = session?.user || null;

          // Clear admin cache on any auth state change
          this._clearAdminCache();

          // Track PASSWORD_RECOVERY events - they may fire before UI listeners are registered
          if (event === 'PASSWORD_RECOVERY') {
            this.pendingPasswordRecovery = true;
            console.log('[AuthService] PASSWORD_RECOVERY event captured');
          }

          this.notifyListeners(event, session);
        } catch (err) {
          console.error('[AuthService] Error in auth state change handler:', err);
        }
      });
      this.authSubscription = subscription;

      this.initialized = true;
      console.log('[AuthService] Initialized', this.currentUser ? 'User logged in' : 'No user');
    } catch (error) {
      console.error('[AuthService] Initialization error:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.currentUser;
  }

  /**
   * Check if current user is anonymous
   * Anonymous users are authenticated but haven't linked an email/OAuth identity
   */
  isAnonymous() {
    return this.currentUser?.is_anonymous === true;
  }

  /**
   * Check if current user is an admin
   * Calls the is_admin() SQL function defined in migrations/005_admin_access.sql
   * Result is cached for the session to avoid repeated DB calls
   * @returns {Promise<boolean>}
   */
  async isAdmin() {
    // Not authenticated = not admin
    if (!this.isAuthenticated() || !this.supabase) {
      return false;
    }

    // Return cached result if available
    if (this._isAdminCached !== undefined) {
      return this._isAdminCached;
    }

    try {
      const { data, error } = await this.supabase.rpc('is_admin');
      if (error) {
        console.warn('[AuthService] Admin check failed:', error);
        return false;
      }
      this._isAdminCached = data === true;
      return this._isAdminCached;
    } catch (err) {
      console.warn('[AuthService] Admin check error:', err);
      return false;
    }
  }

  /**
   * Clear admin status cache (called on auth state change)
   */
  _clearAdminCache() {
    this._isAdminCached = undefined;
  }

  /**
   * Get current user
   */
  getUser() {
    return this.currentUser;
  }

  /**
   * Get current session
   */
  getSession() {
    return this.session;
  }

  /**
   * Get access token for API calls
   */
  getAccessToken() {
    return this.session?.access_token || null;
  }

  /**
   * Get the Supabase client instance
   */
  getClient() {
    return this.supabase;
  }

  /**
   * Sign up with email and password
   */
  async signUp(email, password) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    // Basic email format validation for better UX (faster feedback than server-side)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin
      }
    });

    if (error) throw error;
    return data;
  }

  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  /**
   * Sign in with OAuth provider (Google, etc.)
   */
  async signInWithOAuth(provider) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { data, error } = await this.supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) throw error;
    return data;
  }

  /**
   * Sign in anonymously
   * Creates a new anonymous user with a temporary identity
   * Usage is tracked server-side and preserved when user links an identity
   */
  async signInAnonymously() {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { data, error } = await this.supabase.auth.signInAnonymously();

    if (error) throw error;
    return data;
  }

  /**
   * Link anonymous identity to email/password
   * Called during signup when user is currently anonymous
   * Preserves all usage history and data under the same user ID
   */
  async linkEmailIdentity(email, password) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    if (!this.isAnonymous()) {
      throw new Error('User is not anonymous');
    }

    const { data, error } = await this.supabase.auth.updateUser({
      email,
      password
    });

    if (error) throw error;

    // Call the database function to upgrade the user's tier
    const { error: upgradeError } = await this.supabase.rpc('upgrade_anonymous_user', {
      p_user_id: this.currentUser.id,
      p_email: email
    });

    if (upgradeError) {
      console.error('[AuthService] Error upgrading anonymous user:', upgradeError);
      // Don't throw — auth update succeeded, but surface the error so caller can warn
      return { ...data, upgradeError };
    }

    return data;
  }

  /**
   * Link anonymous identity to OAuth provider
   * Called when anonymous user clicks OAuth sign-in
   * Preserves all usage history and data under the same user ID
   */
  async linkOAuthIdentity(provider) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    if (!this.isAnonymous()) {
      // Not anonymous, do regular OAuth sign-in
      return this.signInWithOAuth(provider);
    }

    const { data, error } = await this.supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) throw error;
    return data;
  }

  /**
   * Sign out
   */
  async signOut() {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { error } = await this.supabase.auth.signOut();
    if (error) throw error;

    this.currentUser = null;
    this.session = null;
  }

  /**
   * Send password reset email
   */
  async resetPassword(email) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { data, error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}?password_recovery=true`
    });

    if (error) throw error;
    return data;
  }

  /**
   * Update password (for logged-in users)
   */
  async updatePassword(newPassword) {
    if (!this.supabase) {
      throw new Error('Auth service not initialized');
    }

    const { data, error } = await this.supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return data;
  }

  /**
   * Get user profile from profiles table
   */
  async getProfile() {
    if (!this.supabase || !this.currentUser) {
      return null;
    }

    const { data, error } = await this.supabase
      .from('profiles')
      .select('*')
      .eq('id', this.currentUser.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('[AuthService] Error fetching profile:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update user profile
   */
  async updateProfile(updates) {
    if (!this.supabase || !this.currentUser) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await this.supabase
      .from('profiles')
      .upsert({
        id: this.currentUser.id,
        ...updates,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of auth state change
   */
  notifyListeners(event, session) {
    this.listeners.forEach(callback => {
      try {
        callback(event, session);
      } catch (error) {
        console.error('[AuthService] Listener error:', error);
      }
    });
  }

  /**
   * Cleanup/dispose auth service
   */
  dispose() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
    this.listeners.clear();
  }
}

// Export singleton instance
export const authService = new AuthService();
